import React, { useEffect, useRef, useState } from "react"
import { Alert, Button, Card, CardBody, Input } from "reactstrap"
import {
  GetTraderRequestMessages,
  SendTraderRequestMessage,
  UploadChatAttachment,
} from "helpers/backend_helper"
import { GetLoggedOnUser, formatDate } from "helpers/misc_helpers"

const TraderRequestChat = ({ requestId }) => {
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState("")
  const [sending, setSending] = useState(false)
  const [uploadingAttachment, setUploadingAttachment] = useState(false)
  const [error, setError] = useState("")
  const [loadingMessages, setLoadingMessages] = useState(true)
  const messagesEndRef = useRef(null)
  const loggedOnUser = GetLoggedOnUser()

  const loadMessages = async () => {
    try {
      const data = await GetTraderRequestMessages(requestId)
      setMessages(data || [])
    } catch (err) {
      console.error("Error loading messages:", err)
    } finally {
      setLoadingMessages(false)
    }
  }

  useEffect(() => {
    loadMessages()
  }, [requestId])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages])



  const handleAttach = async (file) => {
    if (!file) return
    setError("")
    setUploadingAttachment(true)
    try {
      const fd = new FormData()
      fd.append("file", file)
      fd.append("id", String(requestId))
      const res = await UploadChatAttachment(fd)
      if (!res?.url) throw new Error('No url returned')
      await SendTraderRequestMessage(requestId, { fileURL: res.url })
      await loadMessages()
    } catch (err) {
      console.error("Error uploading attachment:", err)
      setError("Failed to upload attachment. Please try again.")
    }
    setUploadingAttachment(false)
  }
  const handleSend = async () => {
    if (!newMessage.trim()) return
    setSending(true)
    setError("")
    try {
      await SendTraderRequestMessage(requestId, { message: newMessage.trim() })
      setNewMessage("")
      await loadMessages()
    } catch (err) {
      console.error("Error sending message:", err)
      setError("Failed to send message. Please try again.")
    }
    setSending(false)
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // JWT enthält nur _id (userID) — deshalb per userID vergleichen, nicht companyID
  // eslint-disable-next-line eqeqeq
  const isSender = (msg) => msg.userID == loggedOnUser?._id

  const getInitials = (name) => {
    if (!name) return "?"
    return name
      .split(" ")
      .map(w => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase()
  }

  return (
    <Card>
      <CardBody>
        <h6 className="mb-3">
          <i className="bx bx-chat me-1"></i>
          Chat
        </h6>

        {error && (
          <Alert color="danger" className="py-2">
            {error}
          </Alert>
        )}

        {/* Message List */}
        <div
          style={{
            height: "400px",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            padding: "16px",
            background: "#f0f2f5",
            borderRadius: "8px",
            marginBottom: "12px",
          }}
        >
          {loadingMessages ? (
            <div className="d-flex justify-content-center align-items-center h-100">
              <i className="bx bx-loader bx-spin text-primary" style={{ fontSize: 28 }}></i>
            </div>
          ) : messages.length === 0 ? (
            <p className="text-muted text-center mt-4">
              No messages yet. Start the conversation!
            </p>
          ) : (
            messages.map(msg => {
              const sender = isSender(msg)
              return (
                <div
                  key={msg.id}
                  className={`d-flex align-items-end gap-2 ${sender ? "flex-row-reverse" : "flex-row"}`}
                >
                  {/* Avatar */}
                  <div
                    className={`rounded-circle d-flex align-items-center justify-content-center text-white flex-shrink-0 ${sender ? "bg-primary" : "bg-secondary"}`}
                    style={{ width: 32, height: 32, fontSize: 11, fontWeight: 700 }}
                  >
                    {getInitials(sender ? "Me" : msg.companyName)}
                  </div>

                  {/* Bubble */}
                  <div style={{ maxWidth: "65%" }}>
                    <div
                      className="text-muted mb-1"
                      style={{ fontSize: 11, textAlign: sender ? "right" : "left" }}
                    >
                      {sender ? "You" : msg.companyName}
                      &nbsp;·&nbsp;
                      {formatDate(msg.dateCreated)}
                    </div>
                    <div
                      className={sender ? "bg-primary text-white" : ""}
                      style={{
                        // Feste Farben statt Theme-Klassen: im Dark Mode wird
                        // .text-dark hell und wäre auf weißem Bubble unlesbar.
                        ...(sender ? {} : { background: "#fff", color: "#212529" }),
                        borderRadius: sender ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                        padding: "10px 14px",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
                        fontSize: 14,
                        lineHeight: 1.5,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {msg.message}
                      {msg.fileURL && (
                        <div className="mt-2">
                          <a
                            href={msg.fileURL}
                            target="_blank"
                            rel="noreferrer"
                            className={sender ? "text-white text-decoration-underline" : "text-primary"}
                          >
                            Attachment
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="d-flex gap-2 align-items-start">
          <label className="btn btn-outline-secondary mb-0">
            <i className="bx bx-paperclip"></i>
            <input
              type="file"
              style={{ display: "none" }}
              onChange={e => handleAttach(e.target.files?.[0])}
              disabled={uploadingAttachment || sending}
            />
          </label>
          <Input
            type="textarea"
            rows={2}
            value={newMessage}
            onChange={e => setNewMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message… (Enter to send, Shift+Enter for new line)"
            disabled={sending}
          />
          <Button
            color="primary"
            onClick={handleSend}
            disabled={sending || uploadingAttachment || !newMessage.trim()}
            style={{ minWidth: "80px" }}
          >
            {sending ? (
              <i className="bx bx-loader bx-spin"></i>
            ) : (
              <i className="bx bx-send"></i>
            )}
          </Button>
        </div>
      </CardBody>
    </Card>
  )
}

export default TraderRequestChat
